import { useEffect, useState } from 'react';
import {
  InspectorSeedRowSchema,
  parseCsvTable,
  type AppUser,
  type InspectorServiceM8Directory,
  type InspectorSeedRow,
} from '@cer-autofill/shared';
import {
  apiErrorReason,
  createUser,
  deleteUser,
  fetchInspectorServiceM8Directory,
  importInspectors,
  updateUser,
} from '../api.js';

type TenancyId = 'actnow' | 'leadingEdge';
type InspectorForm = {
  email: string;
  displayName: string;
  legalName: string;
  mobile: string;
  cerRegistrationNumber: string;
  inspectorId: string;
  status: 'active' | 'disabled';
  actnow: string;
  leadingEdge: string;
};

const emptyForm = (): InspectorForm => ({
  email: '',
  displayName: '',
  legalName: '',
  mobile: '',
  cerRegistrationNumber: '',
  inspectorId: '',
  status: 'active',
  actnow: '',
  leadingEdge: '',
});

export function InspectorManagementScreen() {
  const [directory, setDirectory] = useState<InspectorServiceM8Directory | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [form, setForm] = useState<InspectorForm>(emptyForm);
  const [staffSearch, setStaffSearch] = useState({ actnow: '', leadingEdge: '' });

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setDirectory(await fetchInspectorServiceM8Directory());
    } catch (err) {
      setError(apiErrorReason(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function beginEdit(user: AppUser) {
    setStaffSearch({ actnow: '', leadingEdge: '' });
    setEditingUserId(user.userId);
    setForm({
      email: user.email,
      displayName: user.displayName,
      legalName: user.legalName ?? '',
      mobile: user.mobile ?? '',
      cerRegistrationNumber: user.cerRegistrationNumber ?? '',
      inspectorId: user.inspectorId ?? user.displayName,
      status: user.status,
      actnow: user.serviceM8StaffLinks?.actnow ?? '',
      leadingEdge: user.serviceM8StaffLinks?.leadingEdge ?? user.serviceM8StaffUuid ?? '',
    });
  }

  function resetForm() {
    setStaffSearch({ actnow: '', leadingEdge: '' });
    setEditingUserId(null);
    setForm(emptyForm());
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const details = {
        email: form.email,
        displayName: form.displayName,
        legalName: form.legalName,
        ...(form.mobile ? { mobile: form.mobile } : {}),
        cerRegistrationNumber: form.cerRegistrationNumber,
        inspectorId: form.inspectorId,
        roles: ['inspector'] as AppUser['roles'],
        status: form.status,
        serviceM8StaffLinks: {
          ...(form.actnow ? { actnow: form.actnow } : {}),
          ...(form.leadingEdge ? { leadingEdge: form.leadingEdge } : {}),
        },
      };
      if (editingUserId) {
        const current = directory?.inspectors.find((user) => user.userId === editingUserId);
        await updateUser(editingUserId, {
          ...details,
          roles: current?.roles.includes('inspector') ? current.roles : ['inspector'],
          mobile: form.mobile || null,
          serviceM8StaffLinks: {
            actnow: form.actnow || null,
            leadingEdge: form.leadingEdge || null,
          },
        });
      } else {
        await createUser(details);
      }
      resetForm();
      await load();
    } catch (err) {
      setError(apiErrorReason(err));
    } finally {
      setSaving(false);
    }
  }

  async function remove(user: AppUser) {
    if (!window.confirm(`Delete inspector ${user.displayName} and revoke their sign-in?`)) return;
    setSaving(true);
    try {
      await deleteUser(user.userId);
      if (editingUserId === user.userId) resetForm();
      await load();
    } catch (err) {
      setError(apiErrorReason(err));
    } finally {
      setSaving(false);
    }
  }

  async function importCsv(file: File) {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const table = parseCsvTable(await file.text());
      if (!table) throw new Error('The CSV has no header row.');
      const rows: InspectorSeedRow[] = [];
      const invalid: string[] = [];
      table.records.forEach((row, index) => {
        const candidate = InspectorSeedRowSchema.safeParse({
          displayName: row['display_name'] || row['displayName'] || row['name']?.split(/\s+/)[0],
          legalName: row['legal_name'] || row['legalName'] || row['name'],
          email: row['email'],
          mobile: row['mobile'] || '',
          cerRegistrationNumber: row['cer_registration_number'] || row['cerRegistrationNumber'],
          serviceM8Tenancy: row['servicem8_tenancy'] || row['serviceM8Tenancy'],
          serviceM8StaffName: row['servicem8_staff_name'] || row['serviceM8StaffName'],
          active: !['false', '0', 'inactive'].includes((row['active'] || 'true').toLowerCase()),
        });
        if (candidate.success) rows.push(candidate.data);
        else {
          const label =
            row['display_name'] || row['legal_name'] || row['name'] || `row ${index + 2}`;
          const reason = candidate.error.issues.map((issue) => issue.message).join(', ');
          invalid.push(`${label} (row ${index + 2}): ${reason}`);
        }
      });
      if (rows.length === 0)
        throw new Error(`No valid inspectors to import. ${invalid.join('; ')}`);
      const result = await importInspectors(rows);
      const failures = result.results.filter((item) => item.status === 'error');
      const failureMessages = [
        ...invalid,
        ...failures.map((item) => `${item.email}: ${item.message}`),
      ];
      setNotice(
        failureMessages.length
          ? `${result.results.length - failures.length} imported; ${failureMessages.length} skipped or failed: ${failureMessages.join('; ')}`
          : `${result.results.length} inspectors imported successfully.`,
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Inspector import failed.');
    } finally {
      setSaving(false);
    }
  }

  if (loading && !directory) return <p className="loading">Importing ServiceM8 staff…</p>;

  return (
    <section className="inspector-config">
      <div className="inspector-config__header">
        <div>
          <h3>Inspectors</h3>
          <p className="muted">
            Display name is used in the apps; actual name and CER registration are used for
            generated PDFs.
          </p>
        </div>
        <div className="modal__actions">
          <label className="button-like">
            Import seed CSV
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              disabled={saving}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void importCsv(file);
                event.target.value = '';
              }}
            />
          </label>
          <button type="button" onClick={() => void load()} disabled={loading || saving}>
            Refresh staff
          </button>
          <button type="button" className="primary" onClick={resetForm} disabled={saving}>
            Add inspector
          </button>
        </div>
      </div>
      {error && <p className="error">{error}</p>}
      {notice && <p className="banner banner--success">{notice}</p>}

      <form className="card admin-user-form" onSubmit={(event) => void save(event)}>
        <h3>{editingUserId ? 'Edit inspector' : 'Add inspector'}</h3>
        <div className="filters-grid">
          <Field
            label="Display name"
            value={form.displayName}
            set={(value) => setForm((f) => ({ ...f, displayName: value }))}
            required
          />
          <Field
            label="Actual / PDF name"
            value={form.legalName}
            set={(value) => setForm((f) => ({ ...f, legalName: value }))}
            required
          />
          <Field
            label="Email"
            type="email"
            value={form.email}
            set={(value) => setForm((f) => ({ ...f, email: value }))}
            required
          />
          <Field
            label="Mobile"
            value={form.mobile}
            set={(value) => setForm((f) => ({ ...f, mobile: value }))}
          />
          <Field
            label="CER registration"
            value={form.cerRegistrationNumber}
            set={(value) => setForm((f) => ({ ...f, cerRegistrationNumber: value }))}
            required
          />
          <Field
            label="Inspector key"
            value={form.inspectorId}
            set={(value) => setForm((f) => ({ ...f, inspectorId: value }))}
            required
          />
          <label>
            <span>Status</span>
            <select
              value={form.status}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  status: event.target.value as InspectorForm['status'],
                }))
              }
            >
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </select>
          </label>
          {directory?.tenancies.map((tenancy) => {
            const query = staffSearch[tenancy.id].trim().toLowerCase();
            const matches = tenancy.staff
              .filter(
                (staff) =>
                  staff.active &&
                  [staff.name, staff.email, staff.mobile].some((value) =>
                    value?.toLowerCase().includes(query),
                  ),
              )
              .sort((a, b) => a.name.localeCompare(b.name));
            const selected = tenancy.staff.find((staff) => staff.uuid === form[tenancy.id]);
            const selectionHidden =
              form[tenancy.id] && !matches.some((staff) => staff.uuid === form[tenancy.id]);
            return (
              <div key={tenancy.id} className="staff-link-picker">
                <label htmlFor={`staff-link-${tenancy.id}`}>
                  <span>{tenancy.name} ServiceM8</span>
                </label>
                <input
                  type="search"
                  aria-label={`Search ${tenancy.name} staff`}
                  placeholder="Search name, email or mobile"
                  value={staffSearch[tenancy.id]}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') event.preventDefault();
                  }}
                  onChange={(event) =>
                    setStaffSearch((previous) => ({
                      ...previous,
                      [tenancy.id]: event.target.value,
                    }))
                  }
                />
                <select
                  id={`staff-link-${tenancy.id}`}
                  value={form[tenancy.id]}
                  onChange={(event) => setForm((f) => ({ ...f, [tenancy.id]: event.target.value }))}
                >
                  <option value="">Not linked</option>
                  {selectionHidden && (
                    <option value={form[tenancy.id]} hidden>
                      {selected?.name ?? 'Current link (unavailable)'}
                      {selected && !selected.active ? ' (inactive)' : ''}
                    </option>
                  )}
                  {matches.map((staff) => (
                    <option key={staff.uuid} value={staff.uuid}>
                      {staff.name}
                      {staff.email ? ` — ${staff.email}` : ''}
                    </option>
                  ))}
                </select>
                {query && matches.length === 0 && (
                  <small className="muted" role="status">
                    No active staff match.
                  </small>
                )}
                {selected && !selected.active && (
                  <small className="muted">
                    Current link is inactive. Choose an active staff member or unlink.
                  </small>
                )}
              </div>
            );
          })}
        </div>
        <div className="modal__actions">
          {editingUserId && (
            <button type="button" onClick={resetForm}>
              Cancel
            </button>
          )}
          <button type="submit" className="primary" disabled={saving}>
            {saving ? 'Saving…' : editingUserId ? 'Save inspector' : 'Create inspector'}
          </button>
        </div>
      </form>

      {directory?.inspectors.length ? (
        <div className="inspectors-table" role="table">
          <div className="inspectors-table__row inspectors-table__row--header" role="row">
            <div>Inspector</div>
            <div>Contact / CER</div>
            <div>ServiceM8 links</div>
            <div>Actions</div>
          </div>
          {directory.inspectors.map((inspector) => (
            <div className="inspectors-table__row" role="row" key={inspector.userId}>
              <div>
                <strong>{inspector.displayName}</strong>
                <span className="muted inspector-config__email">
                  {inspector.legalName ?? 'Actual name missing'}
                </span>
              </div>
              <div>
                <span>{inspector.email}</span>
                <span className="muted inspector-config__email">
                  {inspector.mobile || 'No mobile'} ·{' '}
                  {inspector.cerRegistrationNumber || 'No CER registration'}
                </span>
              </div>
              <div>
                {(['leadingEdge', 'actnow'] as TenancyId[]).map((tenancy) =>
                  inspector.serviceM8StaffLinks?.[tenancy] ||
                  (tenancy === 'leadingEdge' && inspector.serviceM8StaffUuid) ? (
                    <span className="pill pill--active" key={tenancy}>
                      {tenancy === 'actnow' ? 'ACTNOW' : 'Leading Edge'}
                    </span>
                  ) : null,
                )}
              </div>
              <div>
                <button type="button" onClick={() => beginEdit(inspector)} disabled={saving}>
                  Edit
                </button>{' '}
                <button
                  type="button"
                  className="danger"
                  onClick={() => void remove(inspector)}
                  disabled={saving}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="empty">No inspectors yet.</p>
      )}
    </section>
  );
}

function Field({
  label,
  value,
  set,
  type = 'text',
  required = false,
}: {
  label: string;
  value: string;
  set: (value: string) => void;
  type?: string;
  required?: boolean;
}) {
  return (
    <label>
      <span>{label}</span>
      <input
        type={type}
        value={value}
        required={required}
        onChange={(event) => set(event.target.value)}
      />
    </label>
  );
}
